import { TeamCount } from "@redotech/account-common/connected-team";
import type { RedoMerchantRpcClient } from "@redotech/merchant-sdk-client/client";
import { FrontendTeamNoticeEvent } from "@redotech/redo-notifications-common/team-notifications";
import { Notification } from "@redotech/redo-support-common/notification";
import { abortRace } from "@redotech/util/abort";
import { sinkPromise } from "@redotech/util/promise";
import { sleep } from "@redotech/util/schedule";
import { assertNever } from "@redotech/util/type";
import { RedoMerchantClient } from "../client";
import {
  getConnectedTeamsCountsStream,
  getTeamNotificationsStreamRpc,
  getUserNotificationsStream,
  listen,
} from "./utils";

type Callback<T> = (value: T) => any | ((value: T) => Promise<any>);
type UnlistenCallback = () => any | (() => Promise<any>);

export enum MerchantAppTopic {
  TEAM = "conversations",
  CONNECTED_TEAMS_COUNTS = "connectedTeamsCounts",
  USER_NOTIFICATIONS = "userNotifications",
}

interface TeamSubscription<T = FrontendTeamNoticeEvent> {
  topic: MerchantAppTopic.TEAM;
  callback: Callback<FrontendTeamNoticeEvent | T>;
}

interface UserSubscription<T = Notification> {
  topic: MerchantAppTopic.USER_NOTIFICATIONS;
  callback: Callback<Notification | T>;
}

interface ConnectedTeamsCountsSubscription<T = TeamCount> {
  topic: MerchantAppTopic.CONNECTED_TEAMS_COUNTS;
  callback: Callback<TeamCount | T>;
}

type SubscribeAndCallOnceCallback =
  | TeamSubscription<null>
  | UserSubscription<null>
  | ConnectedTeamsCountsSubscription<null>;

type EventServerCallback =
  | TeamSubscription
  | UserSubscription
  | ConnectedTeamsCountsSubscription;

export class MerchantAppEventServer {
  private readonly abortController = new AbortController();
  private haveSetupConnectedTeamsCountsListener = false;
  private connectedTeamsCountsListenerAbortController:
    | AbortController
    | undefined = undefined;
  private haveSetupTeamListener = false;
  private userNotificationsSetup = false;
  private latestNotificationId: string | undefined = undefined;
  private teamStreamAbortController: AbortController | undefined = undefined;
  private reconnectAttempts = 0;
  private readonly maxReconnectDelay = 60_000; // 60 seconds max
  private readonly initialReconnectDelay = 1000; // 1 second initial

  constructor(
    private readonly client: RedoMerchantClient,
    private readonly rpcClient: RedoMerchantRpcClient,
  ) {}

  /**
   * Clean up resources when the event server is no longer needed
   */
  cleanup() {
    this.abortController.abort();
    if (this.teamStreamAbortController) {
      this.teamStreamAbortController.abort();
    }
  }

  private async setupTeamListener() {
    this.haveSetupTeamListener = true;
    await this.connectToTeamStream();
  }

  private async connectToTeamStream() {
    try {
      this.teamStreamAbortController?.abort();
      this.teamStreamAbortController = new AbortController();

      const combinedSignal = abortRace([
        this.abortController.signal,
        this.teamStreamAbortController.signal,
      ]);

      // Use "0" for first connection to get all history, then use latest ID for reconnections
      const currentId = this.latestNotificationId || "0";

      for await (const message of getTeamNotificationsStreamRpc({
        rpcClient: this.rpcClient,
        signal: combinedSignal,
        currentId,
      })) {
        this.latestNotificationId = message.id;

        // Reset reconnect attempts on successful message
        this.reconnectAttempts = 0;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const event = message.data as FrontendTeamNoticeEvent;

        for (const callback of Array.from(this.teamTopicListeners.values())) {
          callback(event);
        }
      }
      if (
        !this.abortController.signal.aborted &&
        !this.teamStreamAbortController.signal.aborted
      ) {
        // Stream ended normally (e.g., timeout), reset attempts and reconnect quickly
        this.reconnectAttempts = 0;
        await sleep(Temporal.Duration.from({ seconds: 1 }));
        sinkPromise(this.connectToTeamStream());
      }
    } catch (e: unknown) {
      if (
        !(e instanceof Error && e.name === "AbortError") &&
        !this.abortController.signal.aborted
      ) {
        // Calculate exponential backoff delay
        const delay = Math.min(
          this.initialReconnectDelay * 2 ** this.reconnectAttempts,
          this.maxReconnectDelay,
        );
        this.reconnectAttempts++;
        console.error(
          `Team event stream error, reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`,
          e,
        );
        await sleep(Temporal.Duration.from({ milliseconds: delay }));
        sinkPromise(this.connectToTeamStream());
      }
    }
  }

  private async setupUserNotificationsListener() {
    this.userNotificationsSetup = true;
    try {
      for await (const value of listen({
        query: () =>
          getUserNotificationsStream({
            authorization: this.client.authorization(),
            signal: this.abortController.signal,
          }),
        loopCondition: true,
      })) {
        for (const callback of Array.from(
          this.userNotificationsListeners.values(),
        )) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          callback(value as unknown as Notification);
        }
      }
    } catch (e: unknown) {
      // Retry
      await sleep(Temporal.Duration.from({ seconds: 10 })).then(() => {
        sinkPromise(this.setupUserNotificationsListener());
      });
    }
  }

  private async setupConnectedTeamsCountsListener() {
    this.haveSetupConnectedTeamsCountsListener = true;
    this.connectedTeamsCountsListenerAbortController?.abort();
    this.connectedTeamsCountsListenerAbortController = new AbortController();
    const listenerSignal = abortRace([
      this.abortController.signal,
      this.connectedTeamsCountsListenerAbortController.signal,
    ]);
    try {
      for await (const value of listen({
        query: () =>
          getConnectedTeamsCountsStream({
            authorization: this.client.authorization(),
            signal: listenerSignal,
          }),
        loopCondition: true,
      })) {
        for (const callback of Array.from(
          this.connectedTeamsCountsTopicListeners.values(),
        )) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          callback(value as unknown as TeamCount);
        }
      }
    } catch (e: unknown) {
      if (listenerSignal.aborted) {
        return;
      }
      // Retry
      await sleep(Temporal.Duration.from({ seconds: 10 })).then(() => {
        sinkPromise(this.setupConnectedTeamsCountsListener());
      });
    }
  }

  private readonly userNotificationsListeners = new Set<
    Callback<Notification>
  >();
  private readonly teamTopicListeners = new Set<
    Callback<FrontendTeamNoticeEvent>
  >();
  private readonly connectedTeamsCountsTopicListeners = new Set<
    Callback<TeamCount>
  >();

  /**
   * Publish-subscribe pattern for getting notified when a value is written to a stream. Pass the callback (e.g. a fetch, or setState)
   * that will happen once the write happens, and the callback will be run on writes to the stream.
   * The callback function will immediately be run at the beginning as well, so you can set-and-forget.
   */
  subscribeAndCallOnce(
    subscription: SubscribeAndCallOnceCallback,
  ): UnlistenCallback {
    subscription.callback(null);
    return this.subscribe(subscription);
  }

  subscribe(subscription: EventServerCallback): UnlistenCallback {
    this.runSetup(subscription.topic);
    const { topic, callback } = subscription;
    switch (topic) {
      case MerchantAppTopic.TEAM: {
        this.teamTopicListeners.add(callback);
        const unlistenCallback = () => this.teamTopicListeners.delete(callback);
        return unlistenCallback;
      }
      case MerchantAppTopic.CONNECTED_TEAMS_COUNTS: {
        this.connectedTeamsCountsTopicListeners.add(callback);
        const unlistenCallback = () =>
          this.connectedTeamsCountsTopicListeners.delete(callback);
        return unlistenCallback;
      }
      case MerchantAppTopic.USER_NOTIFICATIONS: {
        this.userNotificationsListeners.add(callback);
        const unlistenCallback = () =>
          this.userNotificationsListeners.delete(callback);
        return unlistenCallback;
      }
      default: {
        assertNever(topic);
      }
    }
  }

  private runSetup(topic: MerchantAppTopic) {
    switch (topic) {
      case MerchantAppTopic.TEAM: {
        if (!this.haveSetupTeamListener) {
          sinkPromise(this.setupTeamListener());
        }

        break;
      }
      case MerchantAppTopic.CONNECTED_TEAMS_COUNTS: {
        if (!this.haveSetupConnectedTeamsCountsListener) {
          sinkPromise(this.setupConnectedTeamsCountsListener());
        }

        break;
      }
      case MerchantAppTopic.USER_NOTIFICATIONS: {
        if (!this.userNotificationsSetup) {
          sinkPromise(this.setupUserNotificationsListener());
        }

        break;
      }
      default: {
        assertNever(topic);
      }
    }
  }
}
